import React, { useState } from 'react';
import {
  Box, Button, FormControl, FormLabel, Input, Textarea, VStack, HStack, IconButton, useToast,
  Heading, Text, Icon, useColorModeValue, Tabs, TabList, Tab, TabPanels, TabPanel,
  Alert, AlertIcon, List, ListItem, ListIcon, Progress, Badge, Stat, StatLabel, StatNumber,
  StatHelpText, SimpleGrid, Tooltip, Divider
} from '@chakra-ui/react';
import { FaTrash, FaPlus, FaCloudUploadAlt, FaDownload, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { MdCheckCircle, MdError, MdWarning } from 'react-icons/md';
import Papa from 'papaparse';
import { addHistoricalFailures } from '../../services/predictiveFailureService';

const HistoricalFailureUploader = () => {
  // State for Manual Entry
  const [manualFailures, setManualFailures] = useState([{ asset_id: '', failure_date: '', notes: '' }]);
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // State for CSV Upload
  const [csvFile, setCsvFile] = useState(null);
  const [isUploadingCsv, setIsUploadingCsv] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Stats
  const [uploadStats, setUploadStats] = useState({
    totalUploaded: 0,
    lastUploadDate: null,
    successRate: 100
  });

  const toast = useToast();
  const cardBg = useColorModeValue('white', 'navy.700');
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const errorBg = useColorModeValue('red.50', 'red.900');
  const successBg = useColorModeValue('green.50', 'green.900');

  const itemBg = useColorModeValue('gray.50', 'gray.700');

  // Validation function
  const validateFailure = (failure, index) => {
    const errors = [];
    if (!failure.asset_id || failure.asset_id.trim() === '') {
      errors.push({ index, field: 'asset_id', message: 'Asset ID is required' });
    }
    if (!failure.failure_date || failure.failure_date.trim() === '') {
      errors.push({ index, field: 'failure_date', message: 'Failure date is required' });
    } else {
      const date = new Date(failure.failure_date);
      if (isNaN(date.getTime())) {
        errors.push({ index, field: 'failure_date', message: 'Invalid date format' });
      } else if (date > new Date()) {
        errors.push({ index, field: 'failure_date', message: 'Date cannot be in the future' });
      }
    }
    return errors;
  };

  // --- Handlers for Manual Entry ---
  const handleInputChange = (index, event) => {
    const values = [...manualFailures];
    values[index][event.target.name] = event.target.value;
    setManualFailures(values);
    
    // Clear validation errors for this field
    setValidationErrors(prev => 
      prev.filter(err => !(err.index === index && err.field === event.target.name))
    );
  };

  const handleAddRow = () => {
    setManualFailures([...manualFailures, { asset_id: '', failure_date: '', notes: '' }]);
  };

  const handleRemoveRow = (index) => {
    const values = [...manualFailures];
    values.splice(index, 1);
    setManualFailures(values);
    setValidationErrors(prev => prev.filter(err => err.index !== index));
  };

  const handleManualSubmit = async () => {
    // Validate all entries
    const allErrors = [];
    manualFailures.forEach((failure, index) => {
      allErrors.push(...validateFailure(failure, index));
    });

    if (allErrors.length > 0) {
      setValidationErrors(allErrors);
      toast({
        title: 'Validation Failed',
        description: `Please fix ${allErrors.length} error(s) before submitting.`,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsSubmittingManual(true);
    try {
      const validFailures = manualFailures.filter(f => f.asset_id && f.failure_date);
      if (validFailures.length === 0) {
        throw new Error("Please fill out at least one complete failure record.");
      }

      const response = await addHistoricalFailures(validFailures);
      
      setUploadStats(prev => ({
        totalUploaded: prev.totalUploaded + (response.insertedCount || validFailures.length),
        lastUploadDate: new Date(),
        successRate: 100
      }));

      toast({
        title: 'Submission Successful',
        description: `${response.insertedCount || validFailures.length} historical records were added successfully.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      setManualFailures([{ asset_id: '', failure_date: '', notes: '' }]);
      setValidationErrors([]);
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.message || 'An error occurred while submitting records.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingManual(false);
    }
  };

  // --- Handlers for CSV Upload ---
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
    
    if (file) {
      // Preview first few rows
      Papa.parse(file, {
        header: true,
        preview: 5,
        complete: (results) => {
          setCsvPreview(results.data);
        }
      });
    }
  };

  const handleCsvUpload = () => {
    if (!csvFile) {
      toast({ 
        title: 'No file selected', 
        description: 'Please select a CSV file first.',
        status: 'warning', 
        duration: 3000 
      });
      return;
    }

    setIsUploadingCsv(true);
    setUploadProgress(0);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          setUploadProgress(25);
          
          const failures = results.data.map((row, index) => {
            if (!row.asset_id || !row.failure_date) {
              throw new Error(`Row ${index + 1}: Missing required field (asset_id or failure_date).`);
            }

            const date = new Date(row.failure_date);
            if (isNaN(date.getTime())) {
              throw new Error(`Row ${index + 1}: Invalid date format "${row.failure_date}".`);
            }
            
            const formattedDate = date.toISOString().split('T')[0];

            return {
              asset_id: row.asset_id.trim(),
              failure_date: formattedDate,
              notes: row.notes || null,
            };
          });

          setUploadProgress(50);

          const response = await addHistoricalFailures(failures);
          
          setUploadProgress(100);
          
          setUploadStats(prev => ({
            totalUploaded: prev.totalUploaded + failures.length,
            lastUploadDate: new Date(),
            successRate: 100
          }));

          toast({
            title: 'Upload Successful',
            description: `Successfully added ${failures.length} records. The AI model can now be retrained with this data.`,
            status: 'success',
            duration: 7000,
            isClosable: true,
          });
          
          setCsvFile(null);
          setCsvPreview(null);
          setUploadProgress(0);
        } catch (error) {
          setUploadProgress(0);
          toast({
            title: 'Upload Failed',
            description: error.message || 'An error occurred during upload.',
            status: 'error',
            duration: 7000,
            isClosable: true,
          });
        } finally {
          setIsUploadingCsv(false);
        }
      },
      error: (error) => {
        toast({ 
          title: 'CSV Parsing Error', 
          description: error.message, 
          status: 'error', 
          duration: 7000 
        });
        setIsUploadingCsv(false);
        setUploadProgress(0);
      },
    });
  };

  const downloadTemplate = () => {
    const template = [
      ['asset_id', 'failure_date', 'notes'],
      ['ASSET-PUMP-001', '2024-01-15', 'Bearing failure after 2000 hours'],
      ['ASSET-MOTOR-042', '2024-02-20', 'Electrical component malfunction'],
      ['ASSET-HVAC-103', '2024-03-10', 'Compressor failure due to overheating']
    ];
    
    const csv = template.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'historical_failures_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast({
      title: 'Template Downloaded',
      description: 'CSV template has been downloaded to your computer.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const getFieldError = (index, field) => {
    return validationErrors.find(err => err.index === index && err.field === field);
  };

  return (
    <Box bg={cardBg} p={6} borderRadius="lg" boxShadow="md" color={textColor} borderWidth="1px" borderColor={borderColor}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="md" mb={2}>Log Historical Failures</Heading>
          <Text fontSize="sm" color="gray.500">
            Add historical failure data to improve predictive model accuracy. This data is essential for training AI to predict future failures.
          </Text>
        </Box>

        {/* Stats Dashboard */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Stat
            bg={successBg}
            p={4}
            borderRadius="md"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <StatLabel fontSize="sm">Total Records Uploaded</StatLabel>
            <StatNumber>{uploadStats.totalUploaded}</StatNumber>
            <StatHelpText>
              <Icon as={FaCheckCircle} color="green.500" mr={1} />
              All time
            </StatHelpText>
          </Stat>
          
          <Stat
            bg={cardBg}
            p={4}
            borderRadius="md"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <StatLabel fontSize="sm">Last Upload</StatLabel>
            <StatNumber fontSize="lg">
              {uploadStats.lastUploadDate 
                ? uploadStats.lastUploadDate.toLocaleDateString() 
                : 'Never'}
            </StatNumber>
            <StatHelpText>
              {uploadStats.lastUploadDate && (
                uploadStats.lastUploadDate.toLocaleTimeString()
              )}
            </StatHelpText>
          </Stat>
          
          <Stat
            bg={cardBg}
            p={4}
            borderRadius="md"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <StatLabel fontSize="sm">Success Rate</StatLabel>
            <StatNumber color="green.500">{uploadStats.successRate}%</StatNumber>
            <StatHelpText>
              <Icon as={MdCheckCircle} color="green.500" mr={1} />
              Upload reliability
            </StatHelpText>
          </Stat>
        </SimpleGrid>

        <Divider />

        {/* Tabbed Interface */}
        <Tabs isFitted variant="enclosed-colored" colorScheme="blue">
          <TabList mb="1em">
            <Tab>
              <Icon as={FaPlus} mr={2} />
              Manual Entry
            </Tab>
            <Tab>
              <Icon as={FaCloudUploadAlt} mr={2} />
              CSV Upload
            </Tab>
          </TabList>
          
          <TabPanels>
            {/* Manual Entry Panel */}
            <TabPanel>
              <VStack spacing={6}>
                {manualFailures.map((failure, index) => (
                  <Box
                    key={index}
                    w="100%"
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={borderColor}
                    bg={itemBg}
                  >
                    <HStack spacing={4} align="flex-start" w="100%">
                      <FormControl isRequired isInvalid={!!getFieldError(index, 'asset_id')} flex="1">
                        <FormLabel fontSize="sm" fontWeight="semibold">Asset ID</FormLabel>
                        <Input
                          name="asset_id"
                          placeholder="e.g., ASSET-PUMP-001"
                          value={failure.asset_id}
                          onChange={(e) => handleInputChange(index, e)}
                          bg={cardBg}
                        />
                        {getFieldError(index, 'asset_id') && (
                          <Text fontSize="xs" color="red.500" mt={1}>
                            {getFieldError(index, 'asset_id').message}
                          </Text>
                        )}
                      </FormControl>
                      
                      <FormControl isRequired isInvalid={!!getFieldError(index, 'failure_date')} flex="1">
                        <FormLabel fontSize="sm" fontWeight="semibold">Failure Date</FormLabel>
                        <Input
                          name="failure_date"
                          type="date"
                          value={failure.failure_date}
                          onChange={(e) => handleInputChange(index, e)}
                          bg={cardBg}
                        />
                        {getFieldError(index, 'failure_date') && (
                          <Text fontSize="xs" color="red.500" mt={1}>
                            {getFieldError(index, 'failure_date').message}
                          </Text>
                        )}
                      </FormControl>
                      
                      <FormControl flex="1.5">
                        <FormLabel fontSize="sm" fontWeight="semibold">Notes (Optional)</FormLabel>
                        <Textarea
                          name="notes"
                          placeholder="Describe the failure details..."
                          value={failure.notes}
                          onChange={(e) => handleInputChange(index, e)}
                          bg={cardBg}
                          size="sm"
                          rows={2}
                        />
                      </FormControl>
                      
                      <Tooltip label="Remove this entry">
                        <IconButton
                          icon={<FaTrash />}
                          aria-label="Remove row"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleRemoveRow(index)}
                          isDisabled={manualFailures.length === 1}
                          mt={8}
                        />
                      </Tooltip>
                    </HStack>
                  </Box>
                ))}

                {validationErrors.length > 0 && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold">Please fix the following errors:</Text>
                      <Text fontSize="sm">{validationErrors.length} validation error(s) found</Text>
                    </Box>
                  </Alert>
                )}
              </VStack>

              <HStack mt={6} justify="space-between">
                <Button
                  leftIcon={<FaPlus />}
                  onClick={handleAddRow}
                  size="sm"
                  variant="outline"
                >
                  Add Another Row
                </Button>
                <Button
                  leftIcon={<Icon as={FaCloudUploadAlt} />}
                  colorScheme="blue"
                  onClick={handleManualSubmit}
                  isLoading={isSubmittingManual}
                  loadingText="Submitting..."
                >
                  Submit {manualFailures.length} Record{manualFailures.length !== 1 ? 's' : ''}
                </Button>
              </HStack>
            </TabPanel>

            {/* CSV Upload Panel */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">CSV File Requirements</Text>
                    <Text fontSize="sm">Your CSV must contain the following columns with exact names:</Text>
                  </Box>
                </Alert>

                <List spacing={2} pl={6}>
                  <ListItem>
                    <ListIcon as={MdCheckCircle} color="green.500" />
                    <b>asset_id</b> - The unique identifier of the asset (required)
                  </ListItem>
                  <ListItem>
                    <ListIcon as={MdCheckCircle} color="green.500" />
                    <b>failure_date</b> - Date in format YYYY-MM-DD (required)
                  </ListItem>
                  <ListItem>
                    <ListIcon as={MdCheckCircle} color="gray.500" />
                    <b>notes</b> - Additional details about the failure (optional)
                  </ListItem>
                </List>

                <Button
                  leftIcon={<Icon as={FaDownload} />}
                  onClick={downloadTemplate}
                  variant="outline"
                  colorScheme="blue"
                  size="sm"
                  alignSelf="flex-start"
                >
                  Download CSV Template
                </Button>

                <Divider />

                <FormControl>
                  <FormLabel fontWeight="semibold">Select CSV File</FormLabel>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    p={1}
                    bg={cardBg}
                  />
                  {csvFile && (
                    <HStack mt={2}>
                      <Badge colorScheme="blue">{csvFile.name}</Badge>
                      <Text fontSize="xs" color="gray.500">
                        ({(csvFile.size / 1024).toFixed(2)} KB)
                      </Text>
                    </HStack>
                  )}
                </FormControl>

                {csvPreview && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>
                      Preview (first 5 rows):
                    </Text>
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      bg={itemBg}
                      fontSize="xs"
                      overflowX="auto"
                    >
                      <pre>{JSON.stringify(csvPreview, null, 2)}</pre>
                    </Box>
                  </Box>
                )}

                {isUploadingCsv && uploadProgress > 0 && (
                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="semibold">Upload Progress</Text>
                      <Text fontSize="sm">{uploadProgress}%</Text>
                    </HStack>
                    <Progress value={uploadProgress} size="sm" colorScheme="blue" hasStripe isAnimated />
                  </Box>
                )}

                <Button
                  colorScheme="blue"
                  onClick={handleCsvUpload}
                  isLoading={isUploadingCsv}
                  loadingText="Processing..."
                  isDisabled={!csvFile}
                  leftIcon={<Icon as={FaCloudUploadAlt} />}
                >
                  Upload and Process Data
                </Button>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};

export default HistoricalFailureUploader;